import { useLayoutEffect, useRef, useState } from "react";

interface ProgramsData {
  buyingPower: string;
  cash: string;
  dailyChange: string;
  stockLending: string;
  highYieldCash: string;
}

interface ProgramsConfig {
  showFpsl: boolean;
  showHyc: boolean;
}

export const useProgramsLayout = (
  items: ProgramsData,
  config: ProgramsConfig,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1000);

  // Layout Constants
  const CHAR_W = 8.5; // Average width per character (approx for text-sm)
  const GAP_SM = 8; // Small gap between items
  const GAP_LG = 20; // Large gap (center divider)
  const PADDING = 48; // Container padding (24px * 2)

  const getW = (text: string) => text.length * CHAR_W;

  // Left Section Width (Balances)
  const wBp = Math.max(getW("Buying Power"), getW(items.buyingPower));
  const wCash = Math.max(getW("Cash"), getW(items.cash));
  const wChange = Math.max(getW("Daily Change"), getW(items.dailyChange));
  const leftWidth = wBp + wCash + wChange + GAP_SM * 2;

  // Right Section Width (Programs)
  let rightWidth = 0;
  let dividerWidth = 0;

  const hasBothPrograms = config.showFpsl && config.showHyc;

  if (config.showFpsl || config.showHyc) {
    dividerWidth = GAP_LG;
    const wSl = config.showFpsl
      ? Math.max(getW("Stock Lending"), getW(items.stockLending))
      : 0;
    const wHyc = config.showHyc
      ? Math.max(getW("High Yield Cash"), getW(items.highYieldCash))
      : 0;
    const gap = hasBothPrograms ? GAP_SM : 0;
    rightWidth = wSl + wHyc + gap;
  }

  const threshold = leftWidth + rightWidth + dividerWidth + PADDING;

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // We use contentRect to get the width content box
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return {
    containerRef,
    isVertical: containerWidth < threshold,
    hasBothPrograms,
  };
};
